/**
 * Utility functions for image handling
 */
/**
 * Convert an image URL to a base64 data URL using Canvas (works better with CORS)
 * @param url - The image URL to convert
 * @returns Promise that resolves to a base64 data URL
 */
export async function urlToBase64(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Enable CORS
        img.onload = () => {
            try {
                // Create canvas and draw image
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }
                ctx.drawImage(img, 0, 0);
                // Convert to base64
                // For BMP files, convert to PNG format which is better supported
                const format = url.toLowerCase().endsWith('.bmp') ? 'image/png' : undefined;
                const dataUrl = canvas.toDataURL(format);
                console.log(`  ✓ Image converted: ${url.split('/').pop()} (${dataUrl.length} bytes)`);
                resolve(dataUrl);
            }
            catch (error) {
                console.error('  ✗ Canvas conversion failed:', error);
                reject(error);
            }
        };
        img.onerror = (error) => {
            console.error('  ✗ Image load failed:', url, error);
            reject(new Error(`Failed to load image: ${url}. This might be a CORS issue. Check Azure Blob Storage CORS settings.`));
        };
        // Start loading the image
        img.src = url;
    });
}
/**
 * Convert multiple image URLs to base64 data URLs
 * @param urls - Array of image URLs to convert
 * @returns Promise that resolves to an array of base64 data URLs
 */
export async function urlsToBase64(urls) {
    return Promise.all(urls.map(url => urlToBase64(url)));
}
/**
 * Preload an image to ensure it's cached
 * @param url - The image URL to preload
 * @returns Promise that resolves when the image is loaded
 */
export function preloadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        img.src = url;
    });
}
